import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Tag } from 'antd';
import { ArrowRightOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import useOpenOnboarding from '../../hooks/onboarding/useOpenOnboarding';

function PhaseCircle({ isComplete }) {
  if (isComplete) {
    return (
      <span
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 border-brand-primary text-brand-primary"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 12 10"
          width="8"
          height="7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="1,5 4,8 11,1" />
        </svg>
      </span>
    );
  }

  return (
    <span className="h-4 w-4 shrink-0 rounded-full border-2 border-brand-inkGrey-grey_3_line" aria-hidden="true" />
  );
}

/**
 * Persistent "Setting up" card for a restaurant owner whose account was
 * provisioned by a partner and whose onboarding is not yet complete
 * (FOO-6351 C10, D4 default entry-point). Shows per-phase completion status
 * so the owner can see at a glance which steps still need attention.
 *
 * `done`: every step the owner can do is done, and the partner takes the
 * restaurant live — the card says so instead of offering to continue.
 */
export default function OwnerSetupCard({ companyId, phases = [], doneCount = 0, done = false, partnerName = null }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Onboarding:OwnerSetupCard' });
  const { t: wizardT } = useTranslation(undefined, { keyPrefix: 'Views:RestaurantOnboarding' });
  const { open: openOnboarding } = useOpenOnboarding();
  const [phasesExpanded, setPhasesExpanded] = useState(false);

  const continueSetup = () => openOnboarding(companyId);

  if (done) {
    return (
      <Card className="m-md" data-testid="owner-setup-card" data-state="done">
        <Tag color="success">{t('done.badge')}</Tag>
        <h5 className="mt-xs mb-none">{t('done.title')}</h5>
        <p className="m-none text-brand-inkGrey-grey_3">
          {t('done.description', { partner: partnerName || t('done.fallback_partner') })}
        </p>
      </Card>
    );
  }

  return (
    <Card className="m-md" data-testid="owner-setup-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1">
          <Tag color="processing">{t('badge')}</Tag>
          <h5 className="mt-xs mb-none">{t('title')}</h5>
          <p className="m-none text-brand-inkGrey-grey_3">{t('description')}</p>
          {phases.length > 0 && (
            <button
              type="button"
              className="mt-2xs flex cursor-pointer items-center gap-1 border-none bg-transparent p-none text-sm text-brand-inkGrey-grey_4"
              aria-expanded={phasesExpanded}
              aria-controls="owner-setup-phases"
              onClick={() => setPhasesExpanded((expanded) => !expanded)}
              data-testid="owner-setup-count"
            >
              {phasesExpanded ? <DownOutlined className="text-xs" /> : <RightOutlined className="text-xs" />}
              {t('count', { done: doneCount, total: phases.length })}
            </button>
          )}

          {phases.length > 0 && phasesExpanded && (
            <ul
              id="owner-setup-phases"
              className="mt-sm list-none m-none p-none space-y-1.5"
              data-testid="owner-setup-phases"
            >
              {phases.map((phase) => (
                <li key={phase.key} className="flex items-center gap-2 text-sm">
                  <PhaseCircle isComplete={phase.isComplete} />
                  <span className={phase.isComplete ? 'text-brand-inkGrey-grey_3 line-through' : undefined}>
                    {wizardT(`phases.${phase.key}.title`)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button type="primary" icon={<ArrowRightOutlined />} onClick={continueSetup}>
          {t('cta')}
        </Button>
      </div>
    </Card>
  );
}
